<template>
  <nav v-if="totalPages > 1" class="blog-pagination" aria-label="博客列表分页导航">
    <button
      class="page-btn prev"
      :disabled="currentPage <= 1"
      :aria-label="prevLabel"
      @click="goTo(currentPage - 1)"
    >
      {{ prevLabel }}
    </button>

    <span class="page-info-mobile" aria-current="page">
      {{ mobileLabel }}
    </span>

    <template v-for="p in visiblePages" :key="String(p)">
      <span
        v-if="p === 'ellipsis'"
        class="page-btn ellipsis"
        aria-hidden="true"
      >...</span>
      <button
        v-else
        class="page-btn"
        :class="{ active: p === currentPage }"
        :aria-current="p === currentPage ? 'page' : undefined"
        :aria-label="`第 ${p} 页`"
        @click="goTo(p)"
      >
        {{ p }}
      </button>
    </template>

    <button
      class="page-btn next"
      :disabled="currentPage >= totalPages"
      :aria-label="nextLabel"
      @click="goTo(currentPage + 1)"
    >
      {{ nextLabel }}
    </button>
  </nav>
</template>

<script setup lang="ts">
import { computed } from 'vue';

const props = defineProps<{
  currentPage: number;
  totalPages: number;
  basePath: string;
  prevLabel: string;
  nextLabel: string;
  mobileLabel: string;
}>();

// 计算可见页码：当前页前后各 1 个 + 首尾页，超出范围用省略号
const visiblePages = computed(() => {
  const total = props.totalPages;
  const current = props.currentPage;
  const pages: Array<number | 'ellipsis'> = [];

  if (total <= 7) {
    for (let i = 1; i <= total; i++) pages.push(i);
    return pages;
  }

  pages.push(1);

  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);

  if (start > 2) pages.push('ellipsis');
  for (let i = start; i <= end; i++) pages.push(i);
  if (end < total - 1) pages.push('ellipsis');

  pages.push(total);

  return pages;
});

function goTo(page: number | string) {
  if (typeof page !== 'number') return;
  if (page < 1 || page > props.totalPages || page === props.currentPage) return;
  if (page === 1) {
    navigateTo(props.basePath);
  } else {
    navigateTo(`${props.basePath}/page/${page}`);
  }
}
</script>

<style scoped lang="scss">
.blog-pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: $spacing-xs;
  margin-top: $spacing-xl;
  flex-wrap: wrap;
}

.page-btn {
  min-width: 40px;
  height: 40px;
  border: 1px solid $gray-300;
  border-radius: $border-radius-sm;
  background: $white;
  color: $gray-700;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover:not(:disabled):not(.active):not(.ellipsis) {
    border-color: $primary-color;
    color: $primary-color;
  }

  &.active {
    background: $primary-color;
    border-color: $primary-color;
    color: $white;
    cursor: default;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  &.ellipsis {
    border: none;
    cursor: default;
    min-width: auto;
    padding: 0 4px;
  }

  &.prev,
  &.next {
    padding: 0 $spacing-md;
  }
}

.page-info-mobile {
  display: none;
  font-size: 14px;
  color: $gray-600;
}

// 移动端：隐藏完整页码，仅显示 上一页 / 第X/Y页 / 下一页
@media (max-width: 768px) {
  .page-btn:not(.prev):not(.next):not(.ellipsis) {
    display: none;
  }
  .page-btn.ellipsis {
    display: none;
  }
  .page-info-mobile {
    display: inline;
  }
}
</style>
